import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { ExportBrowsePage } from '~/features/exports/pages/export-browse-page';
import type { ExportConnector } from '~/features/exports/types/export.types';

/**
 * `connector` is a hint, not a requirement: the browse response echoes the
 * run's own connector, so the page works from a bare URL. Passing it just saves
 * the page one render with no column labels while the first page loads, which is
 * what the drawer's link does.
 */
const searchSchema = z.object({
  connector: z.enum(['GMC', 'META']).optional(),
  /** Human label for the run, for the page heading. Absent on a shared link. */
  label: z.string().optional(),
  /**
   * Comma-separated column keys to start from — the selection the drawer had
   * when its link was opened, and thereafter whatever the page's own column
   * picker applied.
   *
   * A hint, not the source of truth: the response's `runFields` is what makes a
   * BARE url default to the run's attributes. This exists because that only
   * works where the run recorded its selection in `use_case_options`, and
   * carrying the drawer's own selection is both correct and immediate.
   */
  fields: z.string().optional(),
  // ── Everything below is the page's own working state, kept in the URL so a
  // reload or a shared link lands on the same view of the same run. Search text
  // and the per-column view filters are deliberately NOT here: they are
  // transient, and a 10k-id allow-list would not fit in a URL either.
  /** Template code. Absent = the connector's default (raw) template. */
  template: z.string().optional(),
  /** `false` to include failed products; absent means the default, `true`. */
  successOnly: z.boolean().optional(),
  /** Post-enrichment score bounds, 0–10. */
  minScore: z.number().min(0).max(10).optional(),
  maxScore: z.number().min(0).max(10).optional(),
  /** Comma-separated exact category values. */
  categories: z.string().optional(),
  /** Translated locale to export (GMC only). Absent = English. */
  locale: z.string().optional(),
  /** Output format for Start export. Absent = the config's default. */
  format: z.enum(['CSV', 'TSV', 'TXT']).optional(),
  /** Rows per page. The server bounds the browse endpoint to 1–200. */
  pageSize: z.number().int().min(1).max(200).optional(),
});

export type ExportBrowseSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute('/_authenticated/_tenant/exports/$sourceWorkflowId')({
  validateSearch: searchSchema,
  component: RouteComponent,
});

function RouteComponent() {
  const { sourceWorkflowId } = Route.useParams();
  const { connector, label } = Route.useSearch();
  return (
    <ExportBrowsePage
      sourceWorkflowId={sourceWorkflowId}
      {...(connector ? { connector: connector as ExportConnector } : {})}
      {...(label ? { label } : {})}
    />
  );
}
